// A brand's team: who can sign in to the workspace, and what each role does
// (migrations/0050_brand_team.sql). Shared by the Worker and the portal.

import type {Timestamp} from './api.ts';

export type BrandRole = 'admin' | 'manager';

export const BRAND_ROLES: readonly BrandRole[] = ['admin', 'manager'];

export const BRAND_ROLE_LABEL: Record<BrandRole, string> = {admin: 'Admin', manager: 'Manager'};

export const BRAND_ROLE_SUMMARY: Record<BrandRole, string> = {
  admin: 'Everything, including company details, billing, API keys and the team.',
  manager: 'Campaigns, creators, video reviews and messages. Not billing, company details or the team.',
};

/** More than a brand's marketing team needs, and a ceiling for a runaway script. */
export const BRAND_TEAM_LIMIT = 25;

/** An invitation nobody accepted lapses after this; the admin sends it again. */
export const BRAND_INVITE_DAYS = 14;

export function isBrandRole(value: unknown): value is BrandRole {
  return value === 'admin' || value === 'manager';
}

/** The person signed in to the workspace now. */
export interface BrandMe {
  email: string;
  name: string;
  role: BrandRole;
  /** The address the account was registered with: always an Admin, never removed. */
  owner: boolean;
}

export interface BrandTeamMember {
  /** '' for the owner, who is the account itself rather than a member row. */
  id: string;
  email: string;
  name: string;
  role: BrandRole;
  owner: boolean;
  status: 'active' | 'invited' | 'expired';
  invited_by: string;
  invited_at: Timestamp | null;
  joined_at: Timestamp | null;
  last_seen_at: Timestamp | null;
}

export interface BrandTeamResponse {
  members: BrandTeamMember[];
  limit: number;
  /** After a removal or a move to Manager: the person's API keys that stopped with it. */
  keys_revoked?: number;
}

/** POST /api/brand/team: one change, by an Admin; anyone but the owner can leave. */
export type BrandTeamRequest =
  | {action: 'invite'; email: string; name?: string; role: BrandRole}
  | {action: 'role'; id: string; role: BrandRole}
  | {action: 'resend'; id: string}
  | {action: 'remove'; id: string}
  /** A member leaves the team themselves; the owner stays. */
  | {action: 'leave'};
